import { ApiClientError } from './types/ApiClientError';

type RequestResponse<T> = {
	data: T;
	status: number;
	ok: boolean;
};

interface ApiClientConfig {
	baseUrl: string;
	authorization?: string;
	authorizationHeaderName?: string;
}

interface RequestOptions extends RequestInit {
	path: string;
	params?: Record<string, any>;
}

export class ApiClient {
	private readonly BASE_URL: string;
	private readonly AUTHORIZATION_HEADER_NAME: string;
	private authorization: string;
	private headers?: Record<string, string>;

	constructor(config: ApiClientConfig) {
		this.BASE_URL = config.baseUrl;
		this.AUTHORIZATION_HEADER_NAME =
			config.authorizationHeaderName ?? 'Authorization';
		this.authorization = config.authorization ?? '';
	}

	setAuthorization(authorization: string): void {
		this.authorization = authorization;
	}

	setHeaders(headers: Record<string, string>): void {
		this.headers = headers;
	}

	async request<T>(
		requestOptions: RequestOptions,
	): Promise<RequestResponse<T>> {
		const { path, params, headers, ...otherOptions } = requestOptions;
		const requestUrl = this.createUrlWithParams(path, params);
		const requestHeaders = this.createHeaders(headers);

		const response = await fetch(requestUrl, {
			...otherOptions,
			headers: requestHeaders,
		});

		let data = undefined as T;
		try {
			data = (await response.json()) as T;
		} catch (e) {
			if (!response.ok) {
				throw new ApiClientError(e);
			}
		}

		return {
			data,
			status: response.status,
			ok: response.ok,
		};
	}

	private createHeaders(headers: HeadersInit | undefined) {
		if (this.authorization) {
			return {
				[this.AUTHORIZATION_HEADER_NAME]: this.authorization,
				...headers,
				...this.headers,
			};
		}

		if (this.headers) {
			return { ...this.headers, ...headers };
		}

		return headers;
	}

	private createUrlWithParams(path: string, params?: Record<string, any>) {
		const url = new URL(`${this.BASE_URL}${path}`);

		if (params) {
			Object.entries(params).forEach(([key, value]) => {
				if (value === null || value === undefined) {
					return;
				}
				url.searchParams.append(key, <string>value);
			});
		}

		return url.toString();
	}
}
